<script lang="ts" setup>
import avatarPlaceholderImg from '@/assets/img/avatar-placeholder.svg';
import { RequirementsMet } from '@/components';
import { AppAvatar, AppButton, AppFormCard, AppTypography } from '@/components/base';
import { RequirementMetStatus } from '@/enums/RequirementMetStatus';
import dayjs from 'dayjs';
import { ref } from 'vue';


interface WorkExperience {
  position: string;
  employer: string;
  // add other fields as needed
}

interface Service {
  name: string;
  description: string;
}

interface Qualification {
  qualification: {
    name: string;
  };
  issued_at: Date;
  description: string;
}

const props = withDefaults(
  defineProps<{
    avatar?: string;
    workExperiences?: WorkExperience[];
    servicesOffered?: Service[];
    qualifications_needed?: string[];
    isFullyVerified?: boolean;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    requirementsMet?: RequirementMetStatus.NONE | RequirementMetStatus.SOME | RequirementMetStatus.ALL;
    workerId?: number;
    workerListings?: any[];
    availability?: any[];
  }>(),
  {
    avatar: avatarPlaceholderImg,
    requirementsMet: RequirementMetStatus.NONE,
    workExperiences: [],
    servicesOffered: [],
    qualifications_needed: [],
    firstName: '',
    middleName: '',
    lastName: '',
    workerId: 0,
    workerListings: [],
    availability: [],
  }
);

const showVerificationPrompt = ref(false);
const showWorkerDetailsModal = ref(false);
const selectedWorker = ref<any>(null);

const getDayName = (dayNumber: string): string => {
  const dayMap: { [key: string]: string } = {
    '1': 'Monday',
    '2': 'Tuesday', 
    '3': 'Wednesday',
    '4': 'Thursday',
    '5': 'Friday',
    '6': 'Saturday',
    '7': 'Sunday',
    '8': 'Days and Times to be advised'
  };
  return dayMap[dayNumber] || `Day ${dayNumber}`;
};

const formatDaysAndTimes = (daysTime: any[]): string[] => {
  if (!Array.isArray(daysTime)) return [];
  
  return daysTime.map(day => {
    const dayName = day.day_name || getDayName(day.day?.toString() || '');
    
    if (day.start && day.end && !day.is_to_be_advised) {
      return `${dayName}    ${day.start} - ${day.end}`;
    }
    
    if (day.is_to_be_advised) {
      return ` ${dayName} -> To be advised`;
    }
    
    return dayName;
  });
};

function handleObscuredClick() {
  if (!props.isFullyVerified) {
    showVerificationPrompt.value = true;
  }
}

const viewProfile = async (workerId: number): Promise<void> => {
  showWorkerDetailsModal.value = true;
  selectedWorker.value = props.workerListings.find((worker: any) => worker.id === props.workerId) || null;
}

const handleWorkerDetailsModalClose = (): void => {
  showWorkerDetailsModal.value = false;
};


</script>
<template>
  <AppFormCard :no-shadow="false" :no-gutter="true" class="support-worker-card">
    <div class="d-flex mb-2" @click="handleObscuredClick()" style="cursor: pointer;">
      <div>
        <AppAvatar
          size="59"
          :src="avatar"
          :rounded="100"
          class="mr-4"
          :class="{ obscured: !isFullyVerified }"
        />
      </div>
      <div>
        <AppTypography type="subheadline-1" class="mr-1">Name:</AppTypography>
        <AppTypography type="subheadline-2" :class="{ obscured: !isFullyVerified }">
          {{ firstName }}
        </AppTypography>
      </div>
    </div>
    
    <div class="mb-2" v-if="qualifications_needed.length > 0">
      <AppTypography type="subheadline-1" class="mr-1 mb-2">Qualifications:</AppTypography>
      <ul class="qualification-list ml-4">
        <li v-for="(qualification, idx) in qualifications_needed" :key="idx">
        <span>{{ qualification.name }}</span>
        </li>
      </ul>
    </div>
    <div class="d-flex mb-2" v-else>
      <div>
        <AppTypography type="subheadline-1" class="mr-1 mb-2">Qualifications:</AppTypography>
        <AppTypography type="subheadline-2"> -- </AppTypography>
      </div>
    </div>

    <div class="mb-2" v-if="availability.length > 0">
      <AppTypography type="subheadline-1" class="mr-1 mb-2">Times/Days:</AppTypography>
      <div v-if="availability && Array.isArray(availability)" :class="{ obscured: !isFullyVerified }">
          <ul class="day-time-item ">
            <li v-for="(dayTime, index) in formatDaysAndTimes(availability)" :key="index" class="ml-8">
              {{ dayTime }}
            </li>
          </ul>
        </div>
        <div v-else :class="{ obscured: !isFullyVerified }">
          <AppTypography type="body-1">
            Start Date: {{ availability.start_date || '--' }}
          </AppTypography>
          <AppTypography type="body-1">
            End Date: {{ availability.end_date || '--' }}
          </AppTypography> 
        </div>
    </div>
    <div class="d-flex mb-2" v-else >
      <div>
        <AppTypography type="subheadline-1" class="mr-1 mb-2">Times/Days:</AppTypography>
        <AppTypography type="subheadline-2"> -- </AppTypography>
      </div>
    </div>

    <AppButton size="small" block @click="!isFullyVerified ? handleObscuredClick() : viewProfile(workerId)">View Profile</AppButton> 
  </AppFormCard>

  <!-- Popup Modal -->
  <div v-if="showVerificationPrompt" class="modal-overlay" @click.self="showVerificationPrompt = false">
    <div class="modal-content">
      <span class="modal-close" @click="showVerificationPrompt = false" aria-label="Close">&times;</span>
      <div class="modal-message">
        Please verify your ID and complete your profile to view full worker details.
      </div>
    </div>
  </div>

  <!-- Worker Details Modal -->
  <v-dialog
      v-model="showWorkerDetailsModal"
      max-width="600"
      persistent
    >
      <v-card>
        <!-- Header -->
        <v-toolbar density="compact" class="header-toolbar">
          <v-toolbar-title>
            <AppTypography type="title-2" element="h2" class="text-white">
              Worker Details
            </AppTypography>
          </v-toolbar-title>
          <v-btn icon @click="handleWorkerDetailsModalClose" class="text-white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Content -->
        <v-card-text class="pa-6">
          <div v-if="selectedWorker">
            <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Worker Name
              </AppTypography>
              <AppTypography type="body-1">
                {{ selectedWorker.first_name || selectedWorker.preferred_name || '--' }}
              </AppTypography>
            </div>

            <!-- Job Title -->
            <!-- <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Job Title
              </AppTypography>
              <AppTypography type="body-1">
                {{ selectedWorker.title || '--' }}
              </AppTypography>
            </div> -->

            <!-- Description -->
            <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                More About Me
              </AppTypography>
              <AppTypography type="body-1">
                {{ selectedWorker.work_experience || '--' }}
              </AppTypography>
            </div>

            <!-- Days/Times/Dates -->
            <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Days/Times/Dates
              </AppTypography>
              <AppTypography type="body-1" v-if="selectedWorker.availability_schedule && selectedWorker.availability_schedule.length > 0">
                <div v-if="selectedWorker.availability_schedule && Array.isArray(selectedWorker.availability_schedule)">
                  <ul class="day-time-item ">
                    <li v-for="(dayTime, index) in formatDaysAndTimes(selectedWorker.availability_schedule)" :key="index" class="ml-6">
                      {{ dayTime || '--' }}
                    </li>
                  </ul>
                </div>
                <div v-else>
                  <AppTypography type="body-1">
                    Start Date: {{ selectedWorker.start_date || '--' }}
                  </AppTypography>
                  <AppTypography type="body-1">
                    End Date: {{ selectedWorker.end_date || '--' }}
                  </AppTypography> 
                </div>
              </AppTypography>
              <AppTypography type="body-1" v-else>--</AppTypography>
            </div>

            <!-- Location/Suburb -->
            <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Location
              </AppTypography>
              <AppTypography type="body-1">
                {{ selectedWorker.place_state_long_name ||  '--' }}
              </AppTypography>
            </div>


            <!-- Qualifications -->
            <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Services I offer
              </AppTypography>
              <AppTypography type="body-1" class="ml-6" v-if="selectedWorker.services_offered.length > 0">
                <ul class="list-disc list-inside">
                  <li v-for="requirement in selectedWorker.services_offered" :key="requirement.id">
                    {{ requirement.name }}
                  </li>  
                </ul>
              </AppTypography>
              <AppTypography type="body-1" v-else>--</AppTypography>
            </div>

            <!-- Qualifications -->
            <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Qualifications
              </AppTypography>
              <AppTypography type="body-1" class="ml-6" v-if="selectedWorker.qualifications.length > 0">
                <ul class="list-disc list-inside">
                  <li v-for="qualification in selectedWorker.qualifications" :key="qualification.id">
                    {{ qualification.name }}
                  </li>  
                </ul>
              </AppTypography>
              <AppTypography type="body-1" v-else>--</AppTypography>
            </div>

            <!-- Pay Rate -->
            <!-- <div class="mb-6">
              <AppTypography type="title-3" element="h3" class="mb-3">
                Pay Rate
              </AppTypography>
              <AppTypography type="body-1">
                {{ selectedWorker.pay_rate ? (selectedWorker.pay_rate + ' AUD') : '--' }}
              </AppTypography>
            </div> -->
          </div>

          <!-- Error State -->
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-alert-circle-outline</v-icon>
            <AppTypography type="title-3" class="mb-2">Job Not Found</AppTypography>
            <AppTypography type="body-2" color="text-secondary">
              The requested job details could not be loaded.
            </AppTypography>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
</template>
<style lang="scss" scoped>
@use '@/assets/scss/_mixins.scss';

.support-worker-card {
  height: 100%;
  &__desc {
    @include mixins.wrap-text-ellipsis(8);
  }
}

.obscured {
  filter: blur(4px) grayscale(1);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: modal-fade-in 0.2s;
}

@keyframes modal-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  position: relative;
  background: #fff;
  padding: 2.5rem 2rem 2rem 2rem;
  border-radius: 16px;
  min-width: 340px;
  max-width: 604px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18), 0 1.5px 6px rgba(0,0,0,0.08);
  text-align: center;
  animation: modal-content-pop 0.2s;
}

@keyframes modal-content-pop {
  from { transform: scale(0.95); opacity: 0.7; }
  to { transform: scale(1); opacity: 1; }
}

.modal-close {
  position: absolute;
  top: 6px;
  right: 18px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #444;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s, transform 0.1s;
  z-index: 10000;
  line-height: 1;
}
.modal-close:hover {
  color: #e53935;
  transform: scale(1.15);
}

.modal-message {
  font-size: 16px;
  font-weight: 500;
  color: #222;
  letter-spacing: 0.01em;
}

.service-list {
  margin: 0.25rem 0 0 0;
  padding: 0 0 0 1.2em;
  list-style: disc;
}
.service-list li {
  margin-bottom: 0.25em;
  font-size: 1em;
  color: #333;
}
.qualification-list {
  margin: 0.25rem 0 0 0;
  padding: 0 0 0 1.2em;
  list-style: disc;
}
.qualification-list li {
  margin-bottom: 0.25em;
  font-size: 1em;
  color: #333;
}
.header-toolbar {
  background:  #b69ce0;
}
</style>
